import { batch, signal } from "@preact/signals-react"

import { makeClient } from "Pages/DsRepricing/@services/http"


/**
 * Iris's per-user profile, app-wide (CU-869fkmt6r): the widget (`PmsAgentWidget`, the only caller of
 * `DSB`), the launcher ("Set up Iris") and Settings › Iris all read these. Not cleared when Settings
 * unmounts — only `clearIrisProfile()` (logout, or another user) empties it.
 *
 * `GET /api/ia-workspace/profile` → `{ profile, prompt, options }`; `PUT` with the editable profile →
 * `{ profile, prompt }` = what was stored (with `own` / `compare` recomputed). Nothing is optimistic.
 */
const api = makeClient('/api/ia-workspace')

// What is saved, as the server answered; `null` until the first load succeeds
export const irisProfile = signal(null)
// The server's text for Iris — the saved version
export const irisPrompt = signal('')
// The pickers' catalogues (`options` of the GET)
export const irisProfileOptions = signal(null)
// 'idle' | 'loading' | 'ready' | 'error' | 'saving'
export const irisProfileState = signal('idle')
// `{ status, message }` of the last failed load or save
export const irisProfileError = signal(null)
export const irisSavedAt = signal(null)

const errorOf = (e) => ({ status: e?.status || 0, message: e?.message || null })

let loading = null
let saving = null
// a logout in the middle of a request must not repaint the next user with the previous one's answer
let generation = 0

/** Loads once; `force` reloads (after Account saves, or *Try again*) and waits for a save in flight. */
export function loadIrisProfile({ force = false } = {}) {
	if (loading) return loading
	if (saving) return saving.then(() => loadIrisProfile({ force }))
	if (!force && irisProfileState.value === 'ready') return Promise.resolve()

	const mine = generation
	batch(() => {
		irisProfileState.value = 'loading'
		irisProfileError.value = null
	})
	loading = api.get('/profile')
		.then(data => {
			if (mine !== generation) return
			batch(() => {
				irisProfile.value = data?.profile ?? null
				irisPrompt.value = data?.prompt ?? ''
				irisProfileOptions.value = data?.options ?? null
				irisProfileState.value = data?.profile ? 'ready' : 'error'
			})
		})
		.catch(e => {
			if (mine !== generation) return
			// no empty profile in its place: the tab must not show a form that looks saveable (criterion 11)
			batch(() => {
				irisProfileError.value = errorOf(e)
				irisProfileState.value = irisProfile.value ? 'ready' : 'error'
			})
		})
		.finally(() => { if (mine === generation) loading = null })
	return loading
}

/** `PUT` the payload; `true` when saved. The answer — never the payload — becomes the saved profile. */
export function saveIrisProfile(payload) {
	const mine = generation
	irisProfileState.value = 'saving'
	saving = api.put('/profile', payload)
		.then(data => {
			if (mine !== generation) return false
			batch(() => {
				irisProfile.value = data?.profile ?? irisProfile.value
				irisPrompt.value = data?.prompt ?? ''
				irisSavedAt.value = new Date()
				irisProfileError.value = null
				irisProfileState.value = 'ready'
			})
			return true
		})
		.catch(e => {
			if (mine !== generation) return false
			batch(() => {
				irisProfileError.value = errorOf(e)
				irisProfileState.value = 'ready'
			})
			return false
		})
		.finally(() => { if (mine === generation) saving = null })
	return saving
}

export const clearIrisProfile = () => {
	generation += 1
	loading = null
	saving = null
	batch(() => {
		irisProfile.value = null
		irisPrompt.value = ''
		irisProfileOptions.value = null
		irisProfileState.value = 'idle'
		irisProfileError.value = null
		irisSavedAt.value = null
	})
}
